/**
 * Day/night tint via PixiJS `ColorMatrixFilter` on the world container.
 *
 * M2.1: Tint is now driven by `TimeOfDayPhase` + `day_tick` from the
 * ClockState, matching the ambient light model in CLOCK.md §3 / §9.1.
 * Dawn = warm gold, Day = neutral, Dusk = orange, Night = deep blue.
 * Opacity scales with `1.0 - ambientLight` via a cosine half-wave
 * transition during dawn/dusk phases.
 *
 * When the local player is indoors the "indoor" palette replaces the
 * outdoor tint regardless of phase.
 */

import type { Container } from 'pixi.js';
import { ColorMatrixFilter } from 'pixi.js';

import type { TimeOfDayPhase } from '../world/model';

/** Tint is a simple per-channel multiplier in [0, 1]. */
interface Tint {
  r: number;
  g: number;
  b: number;
}

const TINT_NIGHT: Tint = { r: 0.45, g: 0.5, b: 0.75 };
const TINT_DAWN: Tint = { r: 0.95, g: 0.82, b: 0.7 };
const TINT_DAY: Tint = { r: 1, g: 1, b: 1 };
const TINT_DUSK: Tint = { r: 1, g: 0.75, b: 0.55 };
const TINT_INDOOR: Tint = { r: 1, g: 0.9, b: 0.72 };

// Clock config defaults — matches CLOCK.md §11 / config.toml.
const DAWN_START = 200;
const DAY_START = 400;
const DUSK_START = 1400;
const NIGHT_START = 1600;
const NIGHT_LIGHT_FLOOR = 0.05;

/**
 * Ambient light level [0, 1] derived from phase + day_tick.
 * Matches CLOCK.md §3 cosine half-wave model.
 */
export function ambientLight(phase: TimeOfDayPhase, dayTick: number): number {
  switch (phase) {
    case 'Night':
      return NIGHT_LIGHT_FLOOR;
    case 'Day':
      return 1.0;
    case 'Dawn': {
      const progress = (dayTick - DAWN_START) / (DAY_START - DAWN_START);
      const smooth = (1.0 - Math.cos(progress * Math.PI)) * 0.5;
      return NIGHT_LIGHT_FLOOR + smooth * (1.0 - NIGHT_LIGHT_FLOOR);
    }
    case 'Dusk': {
      const progress = (dayTick - DUSK_START) / (NIGHT_START - DUSK_START);
      const smooth = (1.0 - Math.cos(progress * Math.PI)) * 0.5;
      return 1.0 - smooth * (1.0 - NIGHT_LIGHT_FLOOR);
    }
  }
}

/** Compute the outdoor tint for a given phase + day_tick. */
export function outdoorTintFor(phase: TimeOfDayPhase, dayTick: number): Tint {
  const light = ambientLight(phase, dayTick);
  switch (phase) {
    case 'Night':
      return TINT_NIGHT;
    case 'Day':
      return TINT_DAY;
    case 'Dawn':
      // Transition night -> day tint proportional to ambient light.
      return lerp(TINT_NIGHT, lerp(TINT_DAWN, TINT_DAY, light), light);
    case 'Dusk':
      // Transition day -> night tint proportional to 1 - ambient light.
      return lerp(TINT_DAY, lerp(TINT_DUSK, TINT_NIGHT, 1 - light), 1 - light);
  }
}

function lerp(a: Tint, b: Tint, t: number): Tint {
  const c = Math.max(0, Math.min(1, t));
  return {
    r: a.r + (b.r - a.r) * c,
    g: a.g + (b.g - a.g) * c,
    b: a.b + (b.b - a.b) * c,
  };
}

export interface DayNightTinter {
  /** Update the filter's RGB matrix from the current phase + day_tick + indoor flag. */
  apply: (phase: TimeOfDayPhase, dayTick: number, indoor: boolean) => void;
  /** Remove the filter and dispose. */
  destroy: () => void;
}

export function createDayNightTinter(worldContainer: Container): DayNightTinter {
  const filter = new ColorMatrixFilter();
  // PixiJS v8 filters live on `.filters` — assign an array once.
  worldContainer.filters = [filter];

  const apply = (phase: TimeOfDayPhase, dayTick: number, indoor: boolean): void => {
    const t = indoor ? TINT_INDOOR : outdoorTintFor(phase, dayTick);
    // ColorMatrixFilter is a 4x5 matrix; scale the three RGB rows' diagonal
    // by the per-channel tint.
    filter.matrix = [
      t.r, 0, 0, 0, 0,
      0, t.g, 0, 0, 0,
      0, 0, t.b, 0, 0,
      0, 0, 0, 1, 0,
    ];
  };

  return {
    apply,
    destroy: () => {
      worldContainer.filters = [];
      filter.destroy();
    },
  };
}
